import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Fab, Paper, Typography, IconButton, TextField,
  CircularProgress, Avatar, Tooltip, Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { preguntarSuperHook, ejecutarAccionSuperHook } from '../api';
import logoSuperhook from '../assets/logo_superhook.jpeg';

interface Mensaje {
  rol: 'usuario' | 'bot';
  texto: string;
  accion?: any;
  estadoAccion?: 'pendiente' | 'ejecutada' | 'cancelada';
}

const HEI_YELLOW = '#F5C200';

export default function SuperHook() {
  const [abierto, setAbierto] = useState(false);
  const [mensajes, setMensajes] = useState<Mensaje[]>([
    { rol: 'bot', texto: '¡Hola! Soy SuperHook, el asistente del CRM. Puedo ayudarte a consultar máquinas, clientes, recambios e intervenciones. ¿Qué necesitas saber?' }
  ]);
  const [input, setInput] = useState('');
  const [cargando, setCargando] = useState(false);
  const [grabando, setGrabando] = useState(false);
  const finRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);

  // Web Speech API (dictado). Disponible en Chrome/Edge (PC y Android).
  const SpeechRecognitionAPI =
    (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
  const dictadoDisponible = !!SpeechRecognitionAPI;

  const toggleDictado = () => {
    if (grabando) {
      recognitionRef.current?.stop();
      return;
    }
    const rec = new SpeechRecognitionAPI();
    rec.lang = 'es-ES';
    rec.continuous = true;       // sigue escuchando hasta que pares
    rec.interimResults = true;   // muestra el texto mientras hablas

    let textoBase = input; // conserva lo que ya hubiera escrito

    rec.onresult = (event: any) => {
      let transcripcion = '';
      for (let i = 0; i < event.results.length; i++) {
        transcripcion += event.results[i][0].transcript;
      }
      setInput((textoBase ? textoBase + ' ' : '') + transcripcion);
    };
    rec.onerror = (event: any) => {
      if (event.error === 'not-allowed') {
        setMensajes(prev => [...prev, { rol: 'bot', texto: 'No tengo permiso para usar el micrófono. Actívalo en los permisos del navegador (icono del candado junto a la dirección).' }]);
      }
      setGrabando(false);
    };
    rec.onend = () => setGrabando(false);

    recognitionRef.current = rec;
    rec.start();
    setGrabando(true);
  };

  const scrollAbajo = () => finRef.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(() => { scrollAbajo(); }, [mensajes, cargando]);

  const enviar = async () => {
    if (grabando) recognitionRef.current?.stop();
    const pregunta = input.trim();
    if (!pregunta || cargando) return;

    // Historial para que SuperHook tenga contexto de la conversación
    // (los últimos 12 mensajes, sin el saludo inicial)
    const historial = mensajes
      .slice(1)
      .slice(-12)
      .map(m => ({ rol: m.rol, texto: m.texto }));

    setMensajes(prev => [...prev, { rol: 'usuario', texto: pregunta }]);
    setInput('');
    setCargando(true);

    try {
      const data = await preguntarSuperHook(pregunta, historial);
      setMensajes(prev => [...prev, {
        rol: 'bot',
        texto: data.respuesta || 'No he podido generar una respuesta.',
        accion: data.accion || undefined,
        estadoAccion: data.accion ? 'pendiente' : undefined,
      }]);
    } catch (e: any) {
      setMensajes(prev => [...prev, { rol: 'bot', texto: e.response?.data?.detail || 'Lo siento, ha ocurrido un error al procesar tu pregunta.' }]);
    } finally {
      setCargando(false);
    }
  };

  const confirmarAccion = async (indice: number) => {
    const msg = mensajes[indice];
    if (!msg.accion) return;
    setCargando(true);
    try {
      const res = await ejecutarAccionSuperHook({ tipo: msg.accion.tipo, parametros: msg.accion.parametros });
      setMensajes(prev => prev.map((m, i) => i === indice ? { ...m, estadoAccion: 'ejecutada' } : m));
      setMensajes(prev => [...prev, { rol: 'bot', texto: `✅ ${res.mensaje || 'Acción realizada correctamente.'}` }]);
    } catch (e: any) {
      setMensajes(prev => [...prev, { rol: 'bot', texto: `❌ ${e.response?.data?.detail || 'No se ha podido ejecutar la acción.'}` }]);
    } finally {
      setCargando(false);
    }
  };

  const cancelarAccion = (indice: number) => {
    setMensajes(prev => prev.map((m, i) => i === indice ? { ...m, estadoAccion: 'cancelada' } : m));
    setMensajes(prev => [...prev, { rol: 'bot', texto: 'De acuerdo, no lo hago. ¿Necesitas algo más?' }]);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviar();
    }
  };

  return (
    <>
      {/* Botón flotante */}
      {!abierto && (
        <Tooltip title="SuperHook — Asistente IA" placement="left">
          <Fab
            onClick={() => setAbierto(true)}
            sx={{
              position: 'fixed', bottom: 24, right: 24, zIndex: 1300,
              p: 0, overflow: 'hidden', bgcolor: '#0D0D0D',
              '&:hover': { bgcolor: '#0D0D0D', transform: 'scale(1.08)' },
              transition: 'transform 0.15s ease',
            }}
          >
            <img src={logoSuperhook} alt="SuperHook"
              style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scale(1.4)' }} />
          </Fab>
        </Tooltip>
      )}

      {/* Ventana de chat */}
      {abierto && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed', bottom: 24, right: 24, zIndex: 1300,
            width: { xs: 'calc(100vw - 32px)', sm: 380 },
            height: { xs: 'calc(100vh - 120px)', sm: 520 },
            maxHeight: 520,
            display: 'flex', flexDirection: 'column',
            borderRadius: 3, overflow: 'hidden'
          }}
        >
          {/* Cabecera */}
          <Box sx={{
            bgcolor: '#0D0D0D', color: '#FFF', px: 2, py: 1.5,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar src={logoSuperhook} alt="SuperHook"
                imgProps={{ style: { transform: 'scale(1.4)' } }}
                sx={{ bgcolor: '#0D0D0D', width: 32, height: 32 }} />
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>SuperHook</Typography>
                <Typography sx={{ fontSize: 11, color: HEI_YELLOW }}>Asistente IA del CRM</Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={() => setAbierto(false)} sx={{ color: '#FFF' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Mensajes */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: 'background.default' }}>
            {mensajes.map((m, i) => (
              <Box key={i} sx={{
                display: 'flex',
                justifyContent: m.rol === 'usuario' ? 'flex-end' : 'flex-start',
                mb: 1.5
              }}>
                <Box sx={{
                  maxWidth: '85%',
                  bgcolor: m.rol === 'usuario' ? '#4f46e5' : 'background.paper',
                  color: m.rol === 'usuario' ? '#FFF' : 'text.primary',
                  px: 1.5, py: 1, borderRadius: 2,
                  border: m.rol === 'bot' ? '1px solid' : 'none',
                  borderColor: 'divider',
                  whiteSpace: 'pre-wrap', fontSize: 13.5, lineHeight: 1.5,
                  boxShadow: 1
                }}>
                  {m.texto}
                  {m.accion && (
                    <Box sx={{
                      mt: 1, p: 1.2, borderRadius: 1.5,
                      bgcolor: 'rgba(245,194,0,0.12)',
                      border: '1px dashed', borderColor: HEI_YELLOW
                    }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 600, mb: 1 }}>
                        📋 {m.accion.resumen}
                      </Typography>
                      {m.estadoAccion === 'pendiente' && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button size="small" variant="contained" disabled={cargando}
                            onClick={() => confirmarAccion(i)}
                            sx={{ bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}>
                            Confirmar
                          </Button>
                          <Button size="small" variant="outlined" color="inherit" disabled={cargando}
                            onClick={() => cancelarAccion(i)}>
                            Cancelar
                          </Button>
                        </Box>
                      )}
                      {m.estadoAccion === 'ejecutada' && (
                        <Typography sx={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>✅ Confirmada</Typography>
                      )}
                      {m.estadoAccion === 'cancelada' && (
                        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>✖ Cancelada</Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
            {cargando && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', mb: 1.5 }}>
                <CircularProgress size={16} sx={{ color: HEI_YELLOW }} />
                <Typography sx={{ fontSize: 13 }}>SuperHook está pensando...</Typography>
              </Box>
            )}
            <div ref={finRef} />
          </Box>

          {/* Entrada */}
          <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1 }}>
            <TextField
              fullWidth size="small" multiline maxRows={3}
              placeholder="Escribe tu pregunta..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={cargando}
            />
            {dictadoDisponible && (
              <IconButton
                onClick={toggleDictado} disabled={cargando}
                sx={grabando
                  ? { bgcolor: '#ef4444', color: '#FFF', '&:hover': { bgcolor: '#dc2626' },
                      animation: 'pulso 1.2s infinite',
                      '@keyframes pulso': {
                        '0%': { boxShadow: '0 0 0 0 rgba(239,68,68,0.5)' },
                        '70%': { boxShadow: '0 0 0 10px rgba(239,68,68,0)' },
                        '100%': { boxShadow: '0 0 0 0 rgba(239,68,68,0)' },
                      } }
                  : { bgcolor: 'action.hover', color: 'text.primary' }}
              >
                {grabando ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
              </IconButton>
            )}
            <IconButton
              onClick={enviar} disabled={cargando || !input.trim()}
              sx={{ bgcolor: HEI_YELLOW, color: '#0D0D0D', '&:hover': { bgcolor: '#d9ab00' },
                '&.Mui-disabled': { bgcolor: 'action.disabledBackground' } }}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      )}
    </>
  );
}